import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Send, Heart, MessageSquare, User, ArrowLeft,
  Sparkles, X, Hash
} from 'lucide-react';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CircleRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [circle, setCircle] = useState(null);
  const [posts, setPosts] = useState([]);
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('discussion');
  const [newPost, setNewPost] = useState('');
  const [chatMsg, setChatMsg] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const chatEndRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  useEffect(() => { loadCircle(); loadPosts(); loadChat(); }, [id]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat]);

  // Poll chat every 5 seconds
  useEffect(() => {
    if (tab !== 'chat') return;
    const interval = setInterval(loadChat, 5000);
    return () => clearInterval(interval);
  }, [tab, id]);

  const loadCircle = async () => {
    try {
      const res = await axios.get(`${API}/circles/${id}/details?user_id=${user.user_id}`);
      setCircle(res.data);
      const welcomed = localStorage.getItem(`circle_welcomed_${id}`);
      if (!welcomed && res.data.is_member) {
        setShowWelcome(true);
        localStorage.setItem(`circle_welcomed_${id}`, 'true');
      }
    } catch (e) { navigate('/dashboard'); }
    setLoading(false);
  };
  const loadPosts = async () => { try { const r = await axios.get(`${API}/circles/${id}/posts`); setPosts(r.data); } catch(e){} };
  const loadChat = async () => { try { const r = await axios.get(`${API}/circles/${id}/chat`); setChat(r.data); } catch(e){} };

  const handleJoin = async () => {
    try {
      await axios.post(`${API}/circles/${id}/join`, { user_id: user.user_id });
      toast.success('Welcome to the circle!');
      setShowWelcome(true);
      localStorage.setItem(`circle_welcomed_${id}`, 'true');
      loadCircle();
    } catch(e) { toast.error('Failed to join'); }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    try {
      await axios.post(`${API}/circles/${id}/posts`, { user_id: user.user_id, content: newPost });
      setNewPost(''); loadPosts();
    } catch(e) { toast.error('Failed to post'); }
  };

  const handleLike = async (postId) => {
    try {
      const r = await axios.post(`${API}/circles/${id}/posts/${postId}/like`, { user_id: user.user_id });
      setPosts(prev => prev.map(p => p.post_id === postId ? {...p, likes: r.data.likes, liked_by: r.data.liked_by} : p));
    } catch(e) {}
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatMsg.trim()) return;
    try {
      const r = await axios.post(`${API}/circles/${id}/chat`, { user_id: user.user_id, message: chatMsg });
      setChat(prev => [...prev, r.data]);
      setChatMsg('');
    } catch(e) { toast.error('Failed to send'); }
  };

  if (loading) return <AppShell><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-400 rounded-full animate-spin" /></div></AppShell>;

  const COLORS = { purple: 'from-purple-500 to-purple-700', pink: 'from-pink-500 to-pink-700', amber: 'from-amber-500 to-amber-700', emerald: 'from-emerald-500 to-emerald-700', blue: 'from-blue-500 to-blue-700' };
  const gradient = COLORS[circle?.color] || COLORS.purple;

  return (
    <AppShell>
      {/* Grand Welcome */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90" data-testid="grand-welcome">
            <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', duration: 0.8 }} className="text-center px-8 max-w-lg">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}
                className={`w-24 h-24 bg-gradient-to-br ${gradient} rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl`}>
                <Sparkles className="w-12 h-12 text-white" />
              </motion.div>
              <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
                className="text-4xl sm:text-5xl font-cormorant font-bold text-white mb-3">
                Welcome to
              </motion.h1>
              <motion.h2 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}
                className={`text-3xl sm:text-4xl font-cormorant font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent mb-4`}>
                {circle?.name}
              </motion.h2>
              <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9 }}
                className="text-[#A39BB0] font-inter text-sm mb-8 max-w-sm mx-auto">{circle?.description}</motion.p>
              <motion.button initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.1 }}
                onClick={() => setShowWelcome(false)}
                className={`bg-gradient-to-r ${gradient} text-white font-inter font-bold px-10 py-3 rounded-full shadow-lg`}
                data-testid="welcome-enter-btn">
                Enter Circle
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/dashboard')} className="text-[#A39BB0] hover:text-white" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center`}>
              <Hash className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-cormorant font-bold text-white" data-testid="circle-name">{circle?.name}</h1>
              <p className="text-[#A39BB0] text-xs font-inter">{circle?.members} members · {circle?.description}</p>
            </div>
          </div>
          {!circle?.is_member && (
            <motion.button onClick={handleJoin} whileHover={{ scale: 1.05 }}
              className={`bg-gradient-to-r ${gradient} text-white font-inter font-bold px-6 py-2.5 rounded-full text-sm`}
              data-testid="join-circle-btn">Join Circle</motion.button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-white/[0.04] rounded-xl p-1 border border-white/[0.06]">
          {[{ key: 'discussion', label: 'Discussion', icon: MessageSquare }, { key: 'chat', label: 'Live Chat', icon: Send }, { key: 'members', label: 'Members', icon: Users }].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center space-x-2 flex-1 justify-center px-4 py-2.5 rounded-lg text-sm font-inter font-medium transition-all ${
                  tab === t.key ? 'bg-white/10 text-white' : 'text-[#A39BB0] hover:text-white'
                }`} data-testid={`circle-tab-${t.key}`}>
                <Icon className="w-4 h-4" /><span>{t.label}</span>
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Discussion Tab */}
            {tab === 'discussion' && (
              <div className="space-y-4">
                {circle?.is_member && (
                  <form onSubmit={handlePost}>
                    <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-4">
                      <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                        className="w-full bg-transparent text-white placeholder:text-[#A39BB0]/50 outline-none min-h-[60px] resize-none font-inter text-sm"
                        placeholder={`Share with ${circle?.name}...`} data-testid="circle-post-input" />
                      {newPost.trim() && (
                        <div className="flex justify-end pt-3 border-t border-white/[0.06]">
                          <button type="submit" className={`flex items-center space-x-2 bg-gradient-to-r ${gradient} text-white font-inter font-bold px-4 py-2 rounded-full text-xs`} data-testid="circle-post-submit">
                            <Send className="w-3.5 h-3.5" /><span>Post</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </form>
                )}
                {posts.length === 0 ? (
                  <div className="text-center py-12"><p className="text-[#A39BB0] font-inter text-sm">No discussions yet. Start one!</p></div>
                ) : posts.map(post => {
                  const isLiked = (post.liked_by || []).includes(user.user_id);
                  return (
                    <div key={post.post_id} className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5" data-testid={`circle-post-${post.post_id}`}>
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                          {post.author_photo ? <img src={post.author_photo} alt="" className="w-10 h-10 rounded-full object-cover" /> :
                           <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-white" /></div>}
                        </div>
                        <div>
                          <p className="text-white font-inter font-medium text-sm">{post.author_name}</p>
                          <p className="text-[#A39BB0]/50 text-xs font-inter">{new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                      </div>
                      <p className="text-white/80 font-inter text-sm leading-relaxed whitespace-pre-wrap mb-3">{post.content}</p>
                      <button onClick={() => handleLike(post.post_id)}
                        className={`flex items-center space-x-1.5 text-xs font-inter ${isLiked ? 'text-pink-400' : 'text-[#A39BB0] hover:text-pink-400'}`}>
                        <Heart className={`w-4 h-4 ${isLiked ? 'fill-pink-400' : ''}`} /><span>{post.likes || 0}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Chat Tab */}
            {tab === 'chat' && (
              <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] flex flex-col" style={{ height: '500px' }}>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                  {chat.length === 0 ? (
                    <div className="text-center py-12"><p className="text-[#A39BB0] text-sm font-inter">No messages yet</p></div>
                  ) : chat.map(msg => {
                    const isMe = msg.user_id === user.user_id;
                    return (
                      <div key={msg.message_id} className={`flex items-start space-x-2 ${isMe ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                          {msg.author_photo ? <img src={msg.author_photo} alt="" className="w-7 h-7 rounded-full object-cover" /> :
                           <div className="w-7 h-7 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center"><span className="text-white text-[9px] font-bold">{msg.author_name?.charAt(0)}</span></div>}
                        </div>
                        <div className={`max-w-[70%] ${isMe ? 'bg-purple-600/30 border-purple-500/20' : 'bg-white/[0.05] border-white/[0.06]'} rounded-xl px-3 py-2 border`}>
                          {!isMe && <p className="text-purple-300 text-[10px] font-inter font-medium mb-0.5">{msg.author_name}</p>}
                          <p className="text-white/80 text-sm font-inter">{msg.message}</p>
                          <p className="text-[#A39BB0]/40 text-[9px] font-inter mt-1">{new Date(msg.sent_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>
                {circle?.is_member ? (
                  <form onSubmit={handleChat} className="p-3 border-t border-white/[0.06] flex items-center space-x-2">
                    <input value={chatMsg} onChange={e => setChatMsg(e.target.value)}
                      className="flex-1 bg-white/[0.05] border border-white/[0.08] rounded-full px-4 py-2.5 text-sm font-inter text-white placeholder:text-[#A39BB0]/40 outline-none focus:border-purple-500/50"
                      placeholder="Type a message..." data-testid="chat-input" />
                    <button type="submit" disabled={!chatMsg.trim()}
                      className={`w-9 h-9 bg-gradient-to-r ${gradient} rounded-full flex items-center justify-center text-white disabled:opacity-40`}
                      data-testid="chat-send"><Send className="w-4 h-4" /></button>
                  </form>
                ) : (
                  <div className="p-3 border-t border-white/[0.06] text-center"><p className="text-[#A39BB0] text-xs font-inter">Join the circle to chat</p></div>
                )}
              </div>
            )}

            {/* Members Tab */}
            {tab === 'members' && (
              <div className="grid md:grid-cols-2 gap-3">
                {(circle?.member_details || []).map((member, i) => (
                  <div key={member.user_id || i} className="bg-white/[0.07] backdrop-blur-xl rounded-xl border border-white/[0.08] p-4 hover:border-white/15 transition-all cursor-pointer"
                    onClick={() => navigate(`/profile/${member.user_id}`)} data-testid={`circle-member-${i}`}>
                    <div className="flex items-center space-x-3">
                      {member.photo_url ? <img src={member.photo_url} alt="" className="w-11 h-11 rounded-full object-cover" /> :
                       <div className="w-11 h-11 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center"><span className="text-white font-bold">{member.full_name?.charAt(0)}</span></div>}
                      <div className="min-w-0">
                        <h3 className="text-white font-inter font-medium text-sm truncate">{member.full_name}</h3>
                        {member.headline && <p className="text-[#A39BB0] text-xs font-inter truncate">{member.headline}</p>}
                      </div>
                    </div>
                  </div>
                ))}
                {(circle?.member_details || []).length === 0 && (
                  <div className="col-span-2 text-center py-12"><p className="text-[#A39BB0] font-inter text-sm">No members yet. Be the first!</p></div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5">
              <h3 className="text-sm font-inter font-semibold text-white mb-3">About This Circle</h3>
              <p className="text-[#A39BB0] font-inter text-xs leading-relaxed">{circle?.description}</p>
              <div className="mt-4 pt-4 border-t border-white/[0.06]">
                <div className="text-[#A39BB0] text-xs font-inter">{circle?.members} members</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
